import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Add,
  ArrowForward,
  Bolt,
  History,
  Search,
  TravelExplore,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  Dialog,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  PALETTE_ITEMS,
  PaletteGroup,
  detectQueryIntent,
  filterPaletteItems,
  getRecentPages,
  getRecentSearches,
  getSearchableForQuery,
  pushRecentPage,
  pushRecentSearch,
} from '../../config/globalSearchConfig';
import { useGlobalSearch } from '../../contexts/GlobalSearchContext';
import { useAuth } from '../../store/AuthContext';
import {
  canAccessModule,
  getResolvedPermissions,
} from '../../utils/adminPermissions';

const POPULAR_PAGE_IDS = ['home', 'bookings', 'users', 'enquiries', 'tech-all', 'payments', 'leads', 'purchase'];

type ResultItem = {
  id: string;
  label: string;
  sublabel: string;
  group: PaletteGroup | 'Recent';
  action: () => void;
  icon: React.ReactNode;
  badge?: string;
};

const GROUP_ORDER: (PaletteGroup | 'Recent')[] = [
  'Suggested',
  'Recent',
  'Quick actions',
  'Pages',
  'Search data',
];

const groupIcon = (group: PaletteGroup | 'Recent') => {
  switch (group) {
    case 'Suggested':
      return <Bolt sx={{ fontSize: 18, color: '#ffc2d4' }} />;
    case 'Recent':
      return <History sx={{ fontSize: 18, color: '#888' }} />;
    case 'Quick actions':
      return <Add sx={{ fontSize: 18, color: '#7dcea0' }} />;
    case 'Search data':
      return <Search sx={{ fontSize: 18, color: '#aaa' }} />;
    default:
      return <TravelExplore sx={{ fontSize: 18, color: '#aaa' }} />;
  }
};

export function GlobalCommandPalette() {
  const { open, closeSearch, applyLocalFilter } = useGlobalSearch();
  const { authState } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const permissions = useMemo(
    () => getResolvedPermissions(authState),
    [authState.role, authState.department, authState.userType, authState.token],
  );

  const allowedIds = useMemo(() => {
    const ids = new Set<string>();
    PALETTE_ITEMS.forEach((item) => {
      if (!item.module || canAccessModule(permissions, item.module, 'view')) {
        ids.add(item.id);
      }
    });
    return ids;
  }, [permissions]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const goTo = useCallback(
    (path: string, searchText = '', pageLabel?: string) => {
      const trimmed = searchText.trim();
      if (trimmed) pushRecentSearch(trimmed);
      if (pageLabel) pushRecentPage({ id: path, label: pageLabel, path });
      applyLocalFilter(trimmed);
      closeSearch();
      if (trimmed) {
        navigate(`${path}?search=${encodeURIComponent(trimmed)}`);
      } else {
        navigate(path);
      }
    },
    [applyLocalFilter, closeSearch, navigate],
  );

  const intent = useMemo(() => detectQueryIntent(query), [query]);
  const intentLabel =
    intent.type === 'phone'
      ? 'Phone number detected'
      : intent.type === 'email'
        ? 'Email detected'
        : intent.type === 'bookingId'
          ? 'Booking ID detected'
          : null;

  const results: ResultItem[] = useMemo(() => {
    const q = query.trim();
    const items: ResultItem[] = [];

    if (q && location.pathname === '/home') {
      items.push({
        id: 'local-dashboard',
        label: `Filter dashboard bookings for "${q}"`,
        sublabel: 'Stay on dashboard',
        group: 'Suggested',
        icon: groupIcon('Suggested'),
        action: () => {
          pushRecentSearch(q);
          applyLocalFilter(q);
          closeSearch();
        },
      });
    }

    if (q) {
      const topSearch = getSearchableForQuery(q, allowedIds).slice(0, 1)[0];
      if (topSearch) {
        items.push({
          id: 'smart-best',
          label: `Best match: search ${topSearch.label}`,
          sublabel: `"${q}" · ${topSearch.searchHint || topSearch.hint}`,
          group: 'Suggested',
          badge: 'Enter',
          icon: groupIcon('Suggested'),
          action: () => goTo(topSearch.path, q, topSearch.label),
        });
      }
    }

    if (!q) {
      getRecentSearches().forEach((recent) => {
        const mod = getSearchableForQuery(recent, allowedIds)[0];
        items.push({
          id: `recent-search-${recent}`,
          label: recent,
          sublabel: mod ? `Search ${mod.label}` : 'Repeat search',
          group: 'Recent',
          icon: groupIcon('Recent'),
          action: () => goTo(mod?.path || '/bookings', recent, mod?.label),
        });
      });

      getRecentPages().forEach((page) => {
        items.push({
          id: `recent-page-${page.path}`,
          label: page.label,
          sublabel: 'Recently visited',
          group: 'Recent',
          icon: groupIcon('Recent'),
          action: () => goTo(page.path, '', page.label),
        });
      });
    }

    filterPaletteItems(q, allowedIds).forEach((nav) => {
      if (!q && nav.group === 'Pages' && !POPULAR_PAGE_IDS.includes(nav.id)) return;

      if (nav.group === 'Quick actions') {
        items.push({
          id: `action-${nav.id}`,
          label: nav.label,
          sublabel: nav.hint,
          group: 'Quick actions',
          icon: groupIcon('Quick actions'),
          action: () => goTo(nav.path, '', nav.label),
        });
      } else if (nav.group === 'Pages') {
        items.push({
          id: `nav-${nav.id}`,
          label: nav.label,
          sublabel: q ? `Open ${nav.label}` : nav.hint,
          group: 'Pages',
          icon: groupIcon('Pages'),
          action: () => goTo(nav.path, '', nav.label),
        });
      }
    });

    if (q) {
      getSearchableForQuery(q, allowedIds).forEach((mod) => {
        items.push({
          id: `search-${mod.id}`,
          label: `Search ${mod.label} for "${q}"`,
          sublabel: mod.searchHint || mod.hint,
          group: 'Search data',
          icon: groupIcon('Search data'),
          action: () => goTo(mod.path, q, mod.label),
        });
      });
    }

    return items;
  }, [query, location.pathname, allowedIds, goTo, applyLocalFilter, closeSearch]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const grouped = useMemo(() => {
    const map = new Map<PaletteGroup | 'Recent', ResultItem[]>();
    results.forEach((item) => {
      const list = map.get(item.group) || [];
      list.push(item);
      map.set(item.group, list);
    });
    return GROUP_ORDER.filter((g) => map.has(g)).map((g) => ({
      group: g,
      items: map.get(g)!,
    }));
  }, [results]);

  let runningIndex = 0;
  const indexMap = new Map<string, number>();
  grouped.forEach(({ items }) => {
    items.forEach((item) => {
      indexMap.set(item.id, runningIndex);
      runningIndex += 1;
    });
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[activeIndex]) {
      e.preventDefault();
      results[activeIndex].action();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={closeSearch}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1a1a1a',
          backgroundImage: 'none',
          borderRadius: '14px',
          border: '1px solid #333',
          boxShadow: '0 24px 80px rgba(0,0,0,0.55)',
          overflow: 'hidden',
          mt: '10vh',
          alignSelf: 'flex-start',
          maxHeight: '78vh',
        },
      }}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.65)' } } }}
    >
      <Box sx={{ p: 0, display: 'flex', flexDirection: 'column', maxHeight: '78vh' }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          autoFocus
          placeholder="Search pages, users, bookings, phone, email, ID…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#888' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {intentLabel && (
                    <Chip
                      label={intentLabel}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: 10,
                        bgcolor: '#2a2a2a',
                        color: '#bbb',
                        border: '1px solid #444',
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      px: 0.75,
                      py: 0.25,
                      borderRadius: '6px',
                      border: '1px solid #444',
                      fontSize: 11,
                      color: '#888',
                      fontWeight: 600,
                    }}
                  >
                    ESC
                  </Box>
                </Box>
              </InputAdornment>
            ),
            sx: {
              color: '#fff',
              fontSize: 15,
              py: 0.5,
              '& fieldset': { border: 'none' },
            },
          }}
          sx={{
            borderBottom: '1px solid #2c2c2c',
            flexShrink: 0,
            '& .MuiInputBase-root': { px: 2 },
          }}
        />

        <List dense sx={{ overflowY: 'auto', py: 1, flex: 1 }}>
          {results.length === 0 ? (
            <Typography sx={{ px: 2.5, py: 2, color: '#888', fontSize: 14 }}>
              No results for &ldquo;{query}&rdquo;
            </Typography>
          ) : (
            grouped.map(({ group, items }) => (
              <Box key={group}>
                <Typography
                  sx={{
                    px: 2.5,
                    pt: 1.25,
                    pb: 0.5,
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#666',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  {group}
                </Typography>
                {items.map((item) => {
                  const idx = indexMap.get(item.id) ?? 0;
                  return (
                    <ListItemButton
                      key={item.id}
                      selected={idx === activeIndex}
                      onClick={item.action}
                      onMouseEnter={() => setActiveIndex(idx)}
                      sx={{
                        mx: 1,
                        borderRadius: '8px',
                        mb: 0.25,
                        '&.Mui-selected': { bgcolor: '#2a2a2a' },
                        '&.Mui-selected:hover': { bgcolor: '#303030' },
                        '&:hover': { bgcolor: '#252525' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        secondary={item.sublabel}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: '#fff',
                          noWrap: true,
                        }}
                        secondaryTypographyProps={{
                          fontSize: 11,
                          color: '#888',
                          noWrap: true,
                        }}
                      />
                      {item.badge && (
                        <Chip
                          label={item.badge}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 10,
                            mr: 0.5,
                            bgcolor: '#333',
                            color: '#aaa',
                          }}
                        />
                      )}
                      <ArrowForward sx={{ fontSize: 16, color: '#555' }} />
                    </ListItemButton>
                  );
                })}
              </Box>
            ))
          )}
        </List>

        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderTop: '1px solid #2c2c2c',
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            flexShrink: 0,
          }}
        >
          <Hint label="↑↓" text="Navigate" />
          <Hint label="↵" text="Select best match" />
          <Hint label="Ctrl K" text="Toggle" />
        </Box>
      </Box>
    </Dialog>
  );
}

const Hint = ({ label, text }: { label: string; text: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Box
      sx={{
        px: 0.6,
        py: 0.2,
        borderRadius: '5px',
        border: '1px solid #3a3a3a',
        fontSize: 10,
        fontWeight: 700,
        color: '#aaa',
        minWidth: 28,
        textAlign: 'center',
      }}
    >
      {label}
    </Box>
    <Typography sx={{ fontSize: 11, color: '#666' }}>{text}</Typography>
  </Box>
);
